import { Breadcrumbs, Button, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { TreeBreadcrumbSkeleton } from "../../Topbars/DashboardTopbar/PageHeadBreadcrumb/tree-breadcrumbs";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  DrivePanelState,
  GetCurrentObj,
  GetLoadingStates,
  GetPanelObject,
  GetPanelState,
  SetIsSearch,
  SetLoadingStates,
  SetPanelObject,
  SetPanelState,
  ToggleEnlargePanel,
} from "../../../Redux/Reducers/redux-slice-drive";
import { fetchInitialTree, GetTreeObj, GetTreeStatus, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DriveActions } from "./drive-actions";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { SettingsOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { use, useContext, useEffect, useMemo } from "react";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { GetChildRecursivelyObjectById } from "../../../Core/Utils/asset-object-utils";
import { ModalContext } from "../../Modals";
import { assetService } from "../../../Core/Api/services";
import ModalUpdateDefaultPermissions, {
  ModalUpdateDefaultPermissionsElementProps,
} from "../../Modals/ModalUpdateDefaultPermissions/modal-update-default-permissions";
import { SetSearchPattern } from "../../../Redux/Reducers/redux-slice-filters";
import { DriveContext } from "./drive-navigate-handler";

interface IDriveBreadcrumbsProps {}

export const DriveBreadcrumbs = (props: IDriveBreadcrumbsProps) => {
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const driveContext = useContext(DriveContext);

  // Redux
  const dispatch = useAppDispatch();
  const treeObj = useAppSelector(GetTreeObj(TreeContext.DriveTreeview));
  const headObjectId = use(DriveContext)?.headObjectId;
  const currentObj = useAppSelector(GetCurrentObj);
  const panelObj = useAppSelector(GetPanelObject);
  const enlargedPanel = useAppSelector(GetPanelState) === DrivePanelState.Enlarged;
  const treeStatus = useAppSelector(GetTreeStatus(TreeContext.DriveTreeview));

  // Failsafe : when loading directly from Asset URL (not folder), we don't have treeObj cause treeview is not rendered
  // We load it here.
  useEffect(() => {
    if (!treeObj && headObjectId) {
      dispatch(
        fetchInitialTree({ rootId: headObjectId, currentId: currentObj?.id, context: TreeContext.DriveTreeview })
      );
    }
  }, [treeObj]);

  const breadcrumbObj: DbAssetObjectInfos[] = useMemo(() => {
    let final: DbAssetObjectInfos[] = [];
    const obj = enlargedPanel ? panelObj : currentObj;

    if (!treeObj) return [];
    final.push(treeObj);
    if (!obj || !obj.allParentObjectIds || obj.allParentObjectIds.length === 0) return final;
    // we're on root, which equals treeobj; return final
    if ([ObjectType.UploadContainer, ObjectType.GraphSkeleton, ObjectType.VirtualHead].includes(obj.type)) return final;
    obj.allParentObjectIds.map((parentId) => {
      let parent = GetChildRecursivelyObjectById(treeObj, parentId);
      if (parent) final.push(parent);
    });

    final.push(obj);

    return final;
  }, [treeObj, panelObj, currentObj]);

  const openModalEditHeadDefaultPerms = () => {
    if (!openModal || !treeObj) return;

    assetService.GetPermissionsByObjectId(treeObj.id).then((response) => {
      openModal<ModalUpdateDefaultPermissionsElementProps>(ModalUpdateDefaultPermissions, {
        asset: treeObj,
        selectedPermissions: response.defaultPermissionsDto?.permissions || [],
        onSuccess: () => false,
      });
    });
  };

  return (
    <Stack py={1} minHeight={48} direction="row" alignItems="center">
      {treeStatus === "pending" ? (
        <TreeBreadcrumbSkeleton />
      ) : (
        <Breadcrumbs
          separator={"›"}
          //   maxItems={3}
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            li: { display: "inline-flex", flexShrink: 1, minWidth: 0 },
          }}
        >
          {breadcrumbObj &&
            breadcrumbObj.map((node, i) => (
              <div key={`container-${i}`}>
                {i < breadcrumbObj.length - 1 ? (
                  <Link
                    underline="none"
                    component={RouterLink}
                    key={`link-${i}`}
                    to={`${driveContext?.basePath}/${node.id}`}
                    onClick={() => {
                      if (!enlargedPanel) return;
                      dispatch(SetSearchPattern(""));
                      dispatch(SetIsSearch(false));
                      dispatch(ToggleEnlargePanel());
                    }}
                    variant="body1"
                    color="text.secondary"
                    sx={(theme) => ({
                      fontWeight: 400,
                      px: 0.5,
                      py: 0.5,
                      borderRadius: "6px",
                      textWrap: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: theme.palette.backgroundAlpha.hover,
                        color: "text.primary",
                      },
                      transition: theme.transitions.create(["background-color", "color"], {
                        duration: theme.transitions.duration.short,
                        easing: theme.transitions.easing.easeOut,
                      }),
                    })}
                  >
                    {node.name}
                  </Link>
                ) : (
                  <Stack key={`item-${i}`} direction={"row"} alignItems={"center"} gap={0.25} pl={0.5}>
                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                      {node.objectType && <AssetIcon colored type={node.objectType} />}
                      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                        {node.name}
                      </Typography>
                    </Stack>
                    {node.type !== ObjectType.GraphSkeleton &&
                      node.type !== ObjectType.UploadContainer &&
                      node.type !== ObjectType.VirtualHead &&
                      !(treeStatus === "pending") && (
                        <DriveActions
                          object={node}
                          objId={node.id}
                          context={"breadcrumb"}
                          openPanel={() => {
                            dispatch(SetPanelObject(node));
                            dispatch(SetPanelState(DrivePanelState.Preview));
                          }}
                        />
                      )}
                    {(node.type === ObjectType.GraphSkeleton || node.type === ObjectType.UploadContainer) &&
                      UserHasAcl(AclTypes.aclSuperUser) && (
                        <Button startIcon={<SettingsOutlined />} onClick={openModalEditHeadDefaultPerms} sx={{ ml: 1 }}>
                          {t("common:settings")}
                        </Button>
                      )}
                  </Stack>
                )}
              </div>
            ))}
          {/* {panelObj && enlargedPanel && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-start"}
                gap={0.25}
                pl={0.5}
                flexShrink={1}
                minWidth={0}
              >
                <Typography sx={{ textWrap: "wrap", wordBreak: "break-word" }} variant="h5" color="text.primary">
                  {panelObj?.name}
                </Typography>
                <DriveActions objId={panelObj?.id!} />
              </Stack>
            )} */}
        </Breadcrumbs>
      )}
    </Stack>
  );
};
